/**
 * mask-builder.js — region → per-frame masks for the reconstruction engine.
 *
 * Part of /remove-text-from-video. The editor stores overlay regions as
 * {shape:'rect'|'ellipse'|'lasso', x,y,w,h (normalised 0..1), points:[{nx,ny}],
 *  feather, startTime, endTime}. The reconstruction engine needs three derived
 * rasters at PROCESSING resolution for any given time t:
 *
 *   • core   — hard binary mask (255 inside any active region). Drives "which
 *              pixels to reconstruct" and is the authoritative shape.
 *   • reject — core dilated by a few px. Used when sampling a NEIGHBOUR frame to
 *              reject candidates that fall on the overlay's anti-aliased edge
 *              (so a sliver of the old text never leaks into the reconstruction).
 *   • alpha  — feathered 0..1 matte (core, full inside + outward ramp). Used for
 *              the final composite  out = src*(1-alpha) + fill*alpha  so the
 *              reconstructed patch blends seamlessly into the untouched frame.
 *
 * Pure JS (no canvas) so it runs in a Worker without OffscreenCanvas and is
 * node-testable. Loads as `self.RtMaskBuilder` and `module.exports`.
 *
 * Manual refinement: a rotoscope brush (video-bg-remover/roto.js `VbrRoto`) can
 * post-edit `core` per frame via the optional `roto` hook — see build().
 *
 * Silhouettes (2026-09-09): a tracked OBJECT is not a rectangle. A region may
 * carry `masks` — sparse silhouette keyframes {t, x,y,w,h (normalised bbox),
 * mw, mh, rle} produced by the segmenter along the track. At time t the core
 * is the union of the nearest silhouettes before and after t, each warped into
 * the box interpolated from `keyframes`, then dilated by a few percent of the
 * box. Between silhouettes a walking figure changes pose, so the union of two
 * neighbours (rather than one nearest) is what keeps the limb that only one of
 * them saw inside the hole. A region without `masks` rasterises exactly as
 * before — the text tool's rectangles and lassos are untouched.
 */
(function (root, factory) {
    'use strict';
    var api = factory();
    if (typeof module !== 'undefined' && module.exports) module.exports = api;
    if (root) root.RtMaskBuilder = api;
})(typeof self !== 'undefined' ? self : (typeof window !== 'undefined' ? window : null), function () {
    'use strict';

    function activeAt(r, t) {
        if (t == null) return true;
        var s = (r.startTime == null) ? -Infinity : r.startTime;
        var e = (r.endTime == null) ? Infinity : r.endTime;
        return t >= s - 1e-6 && t <= e + 1e-6;
    }

    /* point-in-polygon (ray casting) on normalised points scaled to w,h */
    function inPoly(pts, w, h, px, py) {
        var inside = false;
        for (var i = 0, j = pts.length - 1; i < pts.length; j = i++) {
            var xi = pts[i].nx * w, yi = pts[i].ny * h;
            var xj = pts[j].nx * w, yj = pts[j].ny * h;
            if (((yi > py) !== (yj > py)) && (px < (xj - xi) * (py - yi) / (yj - yi + 1e-12) + xi)) inside = !inside;
        }
        return inside;
    }

    /* ── run-length coding for silhouette keyframes ─────────────────────
       Alternating run lengths over the mw×mh crop, row-major, starting with a
       run of ZEROS (possibly empty). A 480×270 silhouette is ~130 KB raw and a
       few hundred bytes coded; masks cross two worker boundaries per track. */
    function rleEncode(mask, n) {
        var runs = [], cur = 0, len = 0;
        for (var i = 0; i < n; i++) {
            var v = mask[i] ? 1 : 0;
            if (v === cur) { len++; continue; }
            runs.push(len); cur = v; len = 1;
        }
        runs.push(len);
        return Uint32Array.from(runs);
    }
    function rleDecode(rle, n) {
        var out = new Uint8Array(n), i = 0, cur = 0;
        for (var r = 0; r < rle.length && i < n; r++) {
            var len = rle[r];
            if (cur) for (var k = 0; k < len && i < n; k++) out[i++] = 1;
            else i += len;
            cur ^= 1;
        }
        return out;
    }

    /* How much of the SMALLER box lies inside the other. "Same place" for
       two observations of one object that may differ in extent (the body
       alone against the whole figure) — IoU punishes exactly that difference
       (0.67 for body-vs-whole) while a figure walking one step gives about
       the same IoU (0.6), so IoU cannot tell "stood still, seen differently"
       from "moved". Containment can: 1.0 against 0.74. */
    function boxContainN(a, b) {
        var x0 = Math.max(a.x, b.x), y0 = Math.max(a.y, b.y);
        var x1 = Math.min(a.x + a.w, b.x + b.w), y1 = Math.min(a.y + a.h, b.y + b.h);
        var inter = Math.max(0, x1 - x0) * Math.max(0, y1 - y0);
        return inter / Math.max(1e-9, Math.min(a.w * a.h, b.w * b.h));
    }

    /* Indices of the silhouette keyframes bracketing time t (either may equal
       the other at the ends). Masks are sorted by t. */
    function maskPairAt(masks, t) {
        if (!masks || !masks.length) return null;
        var n = masks.length;
        if (t <= masks[0].t) return [0, 0];
        if (t >= masks[n - 1].t) return [n - 1, n - 1];
        var lo = 0, hi = n - 1;
        while (hi - lo > 1) { var mid = (lo + hi) >> 1; if (masks[mid].t <= t) lo = mid; else hi = mid; }
        return [lo, hi];
    }

    /* The track's EXTENT box at time t, interpolated from the keyframes
       (normalised units; the worker's bboxOf without the clamp). */
    function keyframeBoxAt(kf, t) {
        if (!kf || !kf.length) return null;
        if (t <= kf[0].t) return kf[0];
        if (t >= kf[kf.length - 1].t) return kf[kf.length - 1];
        for (var i = 1; i < kf.length; i++) {
            if (t <= kf[i].t) {
                var a = kf[i - 1], b = kf[i], f = (t - a.t) / Math.max(1e-6, b.t - a.t);
                return { x: a.x + (b.x - a.x) * f, y: a.y + (b.y - a.y) * f, w: a.w + (b.w - a.w) * f, h: a.h + (b.h - a.h) * f };
            }
        }
        return kf[kf.length - 1];
    }

    /* Paint one silhouette keyframe into `core`, TRANSPORTED by the track's
       extent motion: the silhouette was observed inside the extent box at its
       own time; it is placed where that box is now (translate + scale between
       the two extent boxes). Decoded masks are memoised on the keyframe.

       ⚠️ Not "warped from its own bbox into the target box": a silhouette's
       own bbox is whatever the segmenter returned THAT sample — the body
       alone one time, body and head the next — and squashing a full figure
       into a body-only box put the head OUTSIDE the hole in every frame whose
       box was the short one. Those head pixels then counted as revealed
       background for the neighbours, and the object's own texture was
       borrowed into the fill (measured on `parked`: checker inside the fill at
       frames 120/180, in the node harness as much as in the browser). */
    function paintSilhouette(core, w, h, m, bx, by, bw, bh, ref) {
        if (!m._dec) m._dec = m.mask || rleDecode(m.rle, m.mw * m.mh);
        var mk = m._dec, mw = m.mw, mh = m.mh;
        // reference extent (px) the silhouette was observed in; without
        // keyframes it is the silhouette's own box (legacy behaviour)
        var rx = ref ? ref.x * w : m.x * w, ry = ref ? ref.y * h : m.y * h;
        var rw = ref ? Math.max(1e-6, ref.w * w) : Math.max(1e-6, m.w * w), rh = ref ? Math.max(1e-6, ref.h * h) : Math.max(1e-6, m.h * h);
        var mx0 = m.x * w, my0 = m.y * h, mwpx = Math.max(1e-6, m.w * w), mhpx = Math.max(1e-6, m.h * h);
        // target region: the transported silhouette bbox, clipped to the frame
        var sx = bw / rw, sy = bh / rh;
        var tx0 = bx + (mx0 - rx) * sx, ty0 = by + (my0 - ry) * sy, tx1 = tx0 + mwpx * sx, ty1 = ty0 + mhpx * sy;
        var x0 = Math.max(0, Math.floor(tx0)), y0 = Math.max(0, Math.floor(ty0));
        var x1 = Math.min(w, Math.ceil(tx1)), y1 = Math.min(h, Math.ceil(ty1));
        for (var y = y0; y < y1; y++) {
            // back to the observation frame, then into the crop
            var oy = ry + (y + 0.5 - by) / sy;
            var v = Math.floor((oy - my0) / mhpx * mh);
            if (v < 0 || v >= mh) continue;
            for (var x = x0; x < x1; x++) {
                var ox = rx + (x + 0.5 - bx) / sx;
                var u = Math.floor((ox - mx0) / mwpx * mw);
                if (u < 0 || u >= mw) continue;
                if (mk[v * mw + u]) core[y * w + x] = 255;
            }
        }
    }

    /* Rasterize the union of active regions into a fresh Uint8 (255 = inside). */
    function rasterize(regions, t, w, h) {
        var core = new Uint8Array(w * h);
        var bx0 = w, by0 = h, bx1 = 0, by1 = 0, any = false, activeCount = 0;

        for (var ri = 0; ri < regions.length; ri++) {
            var r = regions[ri];
            if (!activeAt(r, t)) continue;
            activeCount++;
            var rx0, ry0, rx1, ry1;
            if (r.shape === 'lasso' && r.points && r.points.length >= 3) {
                rx0 = h; ry0 = w; // compute bbox from points
                var minx = 1, miny = 1, maxx = 0, maxy = 0;
                for (var p = 0; p < r.points.length; p++) {
                    if (r.points[p].nx < minx) minx = r.points[p].nx;
                    if (r.points[p].nx > maxx) maxx = r.points[p].nx;
                    if (r.points[p].ny < miny) miny = r.points[p].ny;
                    if (r.points[p].ny > maxy) maxy = r.points[p].ny;
                }
                rx0 = Math.max(0, Math.floor(minx * w)); rx1 = Math.min(w, Math.ceil(maxx * w));
                ry0 = Math.max(0, Math.floor(miny * h)); ry1 = Math.min(h, Math.ceil(maxy * h));
                for (var y = ry0; y < ry1; y++) for (var x = rx0; x < rx1; x++) {
                    if (inPoly(r.points, w, h, x + 0.5, y + 0.5)) core[y * w + x] = 255;
                }
            } else if (r.masks && r.masks.length && maskPairAt(r.masks, t)) {
                // Silhouette track: the interpolated box is the FRAME of the
                // silhouette, the silhouettes bracketing t are painted into it.
                var pair = maskPairAt(r.masks, t);
                var bx = r.x * w, by = r.y * h, bw = r.w * w, bh = r.h * h;
                var sil = new Uint8Array(w * h);
                // The bracketing pair, plus every silhouette whose own box
                // still overlaps this one — i.e. taken while the object stood
                // where it stands now. Segmenter silhouettes disagree with
                // themselves from sample to sample (body with and without the
                // head, IoU 0.63 vs 0.97 on the same standing figure); the
                // NEAREST one alone left the head on screen in every frame
                // whose nearest sample was a body-only one (measured on
                // `parked`, frames 120 and 180). A moving figure overlaps only
                // its immediate neighbours, so the union stays local there;
                // a standing one unions the whole run, which is exactly the
                // "limb one of them saw" safety a removal needs. Leaving a
                // piece of the object is the worst outcome this tool has.
                var here = { x: r.x, y: r.y, w: r.w, h: r.h };
                for (var mi = 0; mi < r.masks.length; mi++) {
                    var mk = r.masks[mi];
                    var isPair = (mi === pair[0] || mi === pair[1]);
                    if (!isPair && boxContainN(mk, here) < 0.8) continue;
                    // A KEEP (/video-bg-remover) wants the silhouette of THIS
                    // moment, not the union of every sample taken here: the
                    // union is a removal's safety («a limb one of them saw»),
                    // and for a keep it pastes every sample's mistake at once.
                    if (!isPair && r.pairOnly) continue;
                    if (isPair) {
                        // the bracketing silhouettes ride the extent's motion to t
                        paintSilhouette(sil, w, h, mk, bx, by, bw, bh, keyframeBoxAt(r.keyframes, mk.t));
                    } else {
                        // a silhouette taken while the object stood HERE is valid
                        // here exactly as observed — painted in place. Transporting
                        // it by the extent motion imported the tracker's per-sample
                        // box wobble (a box centred on a body-only mask sits 9 px
                        // higher) and uncovered the feet in some frames, whose
                        // pixels then passed as revealed background for the
                        // neighbours (measured: 436–756 "real" candidates per
                        // neighbour at t=4.3–4.6 s on a sprite that never moved).
                        paintSilhouette(sil, w, h, mk, mk.x * w, mk.y * h, mk.w * w, mk.h * h, null);
                    }
                }
                // grow: the segmenter's edge is tight and the sample spacing
                // hides motion — a few percent of the box is the honest slack
                // A region may ask for a different slack: /video-bg-remover's
                // «keep this object» must NOT bring a halo of the old room with
                // it, so it asks for ~1 % instead of a removal's 7 %.
                var rad = r.grow != null
                    ? Math.max(1, Math.round(r.grow * Math.max(bw, bh)))
                    : Math.max(3, Math.round(0.07 * Math.max(bw, bh)));
                var grown = dilate(sil, w, h, rad);
                for (var gi = 0; gi < w * h; gi++) if (grown[gi]) core[gi] = 255;
                rx0 = Math.max(0, Math.floor(bx) - rad); ry0 = Math.max(0, Math.floor(by) - rad);
                rx1 = Math.min(w, Math.ceil(bx + bw) + rad); ry1 = Math.min(h, Math.ceil(by + bh) + rad);
            } else {
                rx0 = Math.max(0, Math.floor(r.x * w)); ry0 = Math.max(0, Math.floor(r.y * h));
                rx1 = Math.min(w, Math.ceil((r.x + r.w) * w)); ry1 = Math.min(h, Math.ceil((r.y + r.h) * h));
                if (r.shape === 'ellipse') {
                    var cx = (r.x + r.w / 2) * w, cy = (r.y + r.h / 2) * h;
                    var ax = Math.max(0.5, r.w * w / 2), ay = Math.max(0.5, r.h * h / 2);
                    for (var ey = ry0; ey < ry1; ey++) for (var ex = rx0; ex < rx1; ex++) {
                        var nx = (ex + 0.5 - cx) / ax, ny = (ey + 0.5 - cy) / ay;
                        if (nx * nx + ny * ny <= 1) core[ey * w + ex] = 255;
                    }
                } else {
                    for (var yy = ry0; yy < ry1; yy++) for (var xx = rx0; xx < rx1; xx++) core[yy * w + xx] = 255;
                }
            }
            if (rx0 < bx0) bx0 = rx0; if (ry0 < by0) by0 = ry0;
            if (rx1 > bx1) bx1 = rx1; if (ry1 > by1) by1 = ry1;
            any = true;
        }
        var bbox = any ? { x: bx0, y: by0, w: bx1 - bx0, h: by1 - by0 } : null;
        return { core: core, bbox: bbox, activeCount: activeCount };
    }

    /* Separable max-filter dilation by `rad` px (square structuring element). */
    function dilate(src, w, h, rad) {
        if (rad <= 0) return new Uint8Array(src);
        var tmp = new Uint8Array(w * h), out = new Uint8Array(w * h);
        var x, y, k, m;
        for (y = 0; y < h; y++) {
            for (x = 0; x < w; x++) {
                m = 0;
                for (k = -rad; k <= rad; k++) { var xx = x + k; if (xx >= 0 && xx < w && src[y * w + xx]) { m = 255; break; } }
                tmp[y * w + x] = m;
            }
        }
        for (x = 0; x < w; x++) {
            for (y = 0; y < h; y++) {
                m = 0;
                for (k = -rad; k <= rad; k++) { var yy = y + k; if (yy >= 0 && yy < h && tmp[yy * w + x]) { m = 255; break; } }
                out[y * w + x] = m;
            }
        }
        return out;
    }

    /* Distance (px, capped at 255) from every pixel to the core, 0 inside it —
       a 3-4 chamfer over the core's bbox grown by `reach`; everything farther
       is 255. Lets a caller reject "within m px of the object" for ANY m in
       O(1) per pixel, without one dilation per (neighbour, margin) pair. */
    function distanceToCore(core, w, h, bbox, reach) {
        var out = new Uint8Array(w * h); out.fill(255);
        if (!bbox) return out;
        var x0 = Math.max(0, bbox.x - reach), y0 = Math.max(0, bbox.y - reach);
        var x1 = Math.min(w, bbox.x + bbox.w + reach), y1 = Math.min(h, bbox.y + bbox.h + reach);
        var bw = x1 - x0, bh = y1 - y0;
        if (bw <= 0 || bh <= 0) return out;
        var INF = 1 << 20, d = new Int32Array(bw * bh);
        for (var y = 0; y < bh; y++) for (var x = 0; x < bw; x++) d[y * bw + x] = core[(y0 + y) * w + x0 + x] ? 0 : INF;
        var i, v;
        for (y = 0; y < bh; y++) for (x = 0; x < bw; x++) {
            i = y * bw + x; v = d[i]; if (!v) continue;
            if (x > 0 && d[i - 1] + 3 < v) v = d[i - 1] + 3;
            if (y > 0) {
                if (d[i - bw] + 3 < v) v = d[i - bw] + 3;
                if (x > 0 && d[i - bw - 1] + 4 < v) v = d[i - bw - 1] + 4;
                if (x < bw - 1 && d[i - bw + 1] + 4 < v) v = d[i - bw + 1] + 4;
            }
            d[i] = v;
        }
        for (y = bh - 1; y >= 0; y--) for (x = bw - 1; x >= 0; x--) {
            i = y * bw + x; v = d[i]; if (!v) continue;
            if (x < bw - 1 && d[i + 1] + 3 < v) v = d[i + 1] + 3;
            if (y < bh - 1) {
                if (d[i + bw] + 3 < v) v = d[i + bw] + 3;
                if (x < bw - 1 && d[i + bw + 1] + 4 < v) v = d[i + bw + 1] + 4;
                if (x > 0 && d[i + bw - 1] + 4 < v) v = d[i + bw - 1] + 4;
            }
            d[i] = v;
        }
        for (y = 0; y < bh; y++) for (x = 0; x < bw; x++) {
            v = d[y * bw + x];
            out[(y0 + y) * w + x0 + x] = v >= INF ? 255 : Math.min(255, Math.round(v / 3));
        }
        return out;
    }

    /* Separable box blur of a 0/1 field → Float32 in [0,1]. */
    function boxBlur01(src, w, h, rad) {
        var n = w * h, tmp = new Float32Array(n), out = new Float32Array(n);
        var win = 2 * rad + 1, x, y, k, s;
        for (y = 0; y < h; y++) {
            for (x = 0; x < w; x++) {
                s = 0;
                for (k = -rad; k <= rad; k++) { var xx = x + k; if (xx < 0) xx = 0; else if (xx >= w) xx = w - 1; s += src[y * w + xx] ? 1 : 0; }
                tmp[y * w + x] = s / win;
            }
        }
        for (x = 0; x < w; x++) {
            for (y = 0; y < h; y++) {
                s = 0;
                for (k = -rad; k <= rad; k++) { var yy = y + k; if (yy < 0) yy = 0; else if (yy >= h) yy = h - 1; s += tmp[yy * w + x]; }
                out[y * w + x] = s / win;
            }
        }
        return out;
    }

    /**
     * build(regions, t, w, h, opts) → { core, reject, alpha, bbox, activeCount }
     *   opts.featherPx : outward feather radius in PROC px (default 4)
     *   opts.dilatePx  : reject-mask dilation in PROC px (default 2)
     *   opts.roto      : optional (coreUint8) => coreUint8  hook (rotoscope refine)
     *
     * alpha is forced to 1 over the hard core (region fully replaced) with a soft
     * outward ramp of width ~featherPx for seamless compositing.
     */
    function build(regions, t, w, h, opts) {
        opts = opts || {};
        var featherPx = opts.featherPx == null ? 4 : opts.featherPx;
        var dilatePx = opts.dilatePx == null ? 2 : opts.dilatePx;

        var ras = rasterize(regions, t, w, h);
        var core = ras.core;
        if (typeof opts.roto === 'function') core = opts.roto(core) || core;

        var reject = dilate(core, w, h, dilatePx);
        var dist = opts.withDist ? distanceToCore(core, w, h, ras.bbox, opts.distReach || 24) : null;

        var alpha = new Float32Array(w * h);
        if (featherPx > 0) {
            var blurred = boxBlur01(core, w, h, featherPx);
            for (var i = 0; i < alpha.length; i++) alpha[i] = core[i] ? 1 : blurred[i];
        } else {
            for (var j = 0; j < alpha.length; j++) alpha[j] = core[j] ? 1 : 0;
        }
        return { core: core, reject: reject, alpha: alpha, bbox: ras.bbox, activeCount: ras.activeCount, dist: dist };
    }

    return { rasterize: rasterize, dilate: dilate, boxBlur01: boxBlur01, build: build, activeAt: activeAt, distanceToCore: distanceToCore, keyframeBoxAt: keyframeBoxAt,
             rleEncode: rleEncode, rleDecode: rleDecode, maskPairAt: maskPairAt, paintSilhouette: paintSilhouette };
});
